import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/cms/ui/dialog';
import { DeviceMobileIcon, GlobeIcon } from '@phosphor-icons/react';
import LureoButton from '@/common/LureoButton';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { TestInMobileAppDialog } from './test-in-mobile-app-dialog';

export type PrePublishCheckSurface = 'story_edit_page' | 'projects_page';

interface PrePublishCheckDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user confirms they have tested and want to proceed. */
  onProceed: () => void;
  /** Noun used in copy: 'story' on StoryEdit, 'project' on ProjectDetail. */
  noun: 'story' | 'project';
  /** Where this gate was triggered, recorded for analytics. */
  surface: PrePublishCheckSurface;
  /** Optional ids for analytics correlation. */
  projectId?: string;
  sceneInstanceId?: string;
  /** When set, "Show me how to test" defers to this share dialog instead of the legacy instructions. */
  onShowTest?: () => void;
}

/**
 * Step-zero gate before the existing publish flow. Asks the user to confirm
 * they actually mean to publish, since creators commonly publish in order to
 * "test" their story when there's a separate mobile-app test path.
 *
 * Design choice: always-on, with the "proceed" button focused so an experienced
 * user can press Enter and continue without disruption.
 */
export function PrePublishCheckDialog({
  open,
  onOpenChange,
  onProceed,
  noun,
  surface,
  projectId,
  sceneInstanceId,
  onShowTest,
}: PrePublishCheckDialogProps) {
  const services = useServices();
  const [showInstructions, setShowInstructions] = useState(false);
  const proceedRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    services.analytics.trackLureoPrePublishCheckShown({
      surface,
      noun,
      project_id: projectId,
      scene_instance_id: sceneInstanceId,
    });
  }, [open, surface, noun, projectId, sceneInstanceId, services.analytics]);

  // Focus the "proceed" button on open so Enter continues the publish flow.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => proceedRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  // Close this dialog first, wait for its exit animation to finish, then run
  // `next`. Without the wait, two overlays stack and the screen visibly darkens.
  // Matches the dialog's duration-moderate (200ms) fade-out.
  const closeThenRun = (next: () => void) => {
    onOpenChange(false);
    window.setTimeout(next, 200);
  };

  const handleSeeInstructions = () => {
    services.analytics.trackLureoPrePublishCheckResolved({
      surface,
      choice: 'view_instructions',
      project_id: projectId,
      scene_instance_id: sceneInstanceId,
    });
    closeThenRun(onShowTest ?? (() => setShowInstructions(true)));
  };

  const handleProceed = () => {
    services.analytics.trackLureoPrePublishCheckResolved({
      surface,
      choice: 'proceed_to_publish',
      project_id: projectId,
      scene_instance_id: sceneInstanceId,
    });
    closeThenRun(onProceed);
  };

  const handleCancel = () => {
    services.analytics.trackLureoPrePublishCheckResolved({
      surface,
      choice: 'cancel',
      project_id: projectId,
      scene_instance_id: sceneInstanceId,
    });
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : handleCancel())}>
        <DialogContent scrim="dim" className="max-w-[560px] cursor-default select-none">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <DeviceMobileIcon className="h-6 w-6 text-lureo-sky-ink" weight="regular" />
              <DialogTitle className="tracking-tight">Publishing, or just testing?</DialogTitle>
            </div>
            <DialogDescription className="pt-2 leading-relaxed">
              Publishing makes this {noun} <strong>public</strong>. If you wanted to
              try it on your phone first, you don't need to publish. There's a
              faster way to test inside the Lureo mobile app.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="sm:gap-4">
            <LureoButton
              variant="outline-2"
              suppressTooltip
              className="h-9 px-4 rounded-lureo-radius-button-pill whitespace-nowrap"
              onClick={handleSeeInstructions}
            >
              <DeviceMobileIcon className="h-5 w-5" weight="regular" />
              Show me how to test
            </LureoButton>
            <LureoButton
              ref={proceedRef}
              variant="secondary"
              suppressTooltip
              className="h-9 px-4 rounded-lureo-radius-button-pill whitespace-nowrap"
              onClick={handleProceed}
            >
              <GlobeIcon className="h-5 w-5" weight="regular" />
              Yes, publish
            </LureoButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TestInMobileAppDialog
        open={showInstructions}
        onOpenChange={setShowInstructions}
        noun={noun}
        source="pre_publish_check"
        projectId={projectId}
        sceneInstanceId={sceneInstanceId}
      />
    </>
  );
}
